/* eslint-disable @stylistic/max-len */
import type {RefObject} from 'react';

/**
 * The `CandleIconProps` interface defines the properties for the CandleIcon component.
 */
interface CandleIconProps {
    /** CSS class to override default styling. */
    className?: string;
    /** Primary color of the icon. */
    color1?: string;
    /** Height of the icon. */
    height?: number | string;
    ref?: RefObject<SVGSVGElement>;
    /** Test ID for Cypress. */
    testId?: string;
    /** Width of the icon. */
    width?: number | string;
}

/**
 * Material Symbol "candle" — outlined, weight 300.
 * Candle icon for the Erinnerungskultur (culture of remembrance) category.
 *
 * @param props           The component props.
 * @param props.className CSS class override.
 * @param props.color1    Primary icon color.
 * @param props.height    Icon height.
 * @param props.ref       SVG element ref.
 * @param props.testId    Test identifier.
 * @param props.width     Icon width.
 * @returns A React SVG element.
 */
export const CandleIcon = ({
    className = '',
    color1 = 'currentColor',
    height,
    ref,
    testId = 'CandleIcon',
    width
}: CandleIconProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        fill={color1}
        height={height}
        viewBox="0 -960 960 960"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M248.46-160h465.39q20.59 0 34.53-13.93 13.93-13.94 13.93-34.53v-8.85H200v8.85q0 20.59 13.93 34.53Q227.87-160 248.46-160Zm161.35-517.35q-28.73-30.03-26.73-72.65 2-47 32.46-83.04Q446-869.08 481.15-900q35.16 30.92 65.62 66.96 30.46 36.04 32.46 82.65 2 43.03-26.67 73.05-28.67 30.03-71.35 30.03-42.67 0-71.4-30.04Zm23.65 400.04h95.39v-240.38q0-5.39-3.46-8.85-3.47-3.46-8.85-3.46h-70.77q-5.39 0-8.85 3.46t-3.46 8.85v240.38Zm74.96-441.45q10.81-11.46 10.81-28.01 0-20.85-12.19-37.73-12.19-16.89-25.89-32.58-13.69 15.69-25.88 32.58-12.19 16.88-12.19 37.73 0 16.55 10.78 28.01 10.78 11.45 27.27 11.45 16.49 0 27.29-11.45ZM829-288.31q11-11 11-27.27t-11-27.27q-11-11-27.08-11-16.07 0-27.07 11-11.01 11-11.01 27.27t11.01 27.27q11 11 27.07 11 16.08 0 27.08-11ZM713.85-100H248.46q-45.19 0-76.82-31.64Q140-163.27 140-208.46v-68.85h233.46v-240.38q0-29.83 21.24-51.07Q415.94-590 445.77-590h70.77q29.82 0 51.07 21.24 21.24 21.24 21.24 51.07v240.38h124.77q-4.7-9.15-7.23-18.48-2.54-9.32-2.54-19.98 0-41.01 28.52-69.54 28.53-28.54 69.54-28.54 41.01 0 69.55 28.54Q900-356.78 900-315.77q0 35.69-22.12 62.38-22.11 26.7-55.57 33.62v11.31q0 45.19-31.64 76.82Q759.04-100 713.85-100Zm-232.7-60Zm-47.69-117.31H528.85h-95.39Zm47.69-484.77Z" />
    </svg>
);

CandleIcon.displayName = 'CandleIcon';